import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Modal,
  TextField,
  Box,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import Sidebar from "./Sidebar";
import AddIcon from "@mui/icons-material/Add";
import UploadIcon from "@mui/icons-material/Upload";
import MoreVertIcon from "@mui/icons-material/MoreVert";

const CategoryManagement = () => {
  const [openModal, setOpenModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [category, setCategory] = useState("");
  const [subcategories, setSubcategories] = useState([{ subCategory: "", subCategoryId: null }]);
  const [categoryList, setCategoryList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(5);
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [error, setError] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleOpenEditModal = (category) => {
    setEditMode(true);
    setCategory(category.category);
    setSubcategories(
      category.propertySubCategories.map((sub) => ({
        subCategory: sub.subCategory,
        subCategoryId: sub.subCategoryId,
      }))
    );
    setSelectedCategory(category);
    setOpenModal(true);
  };

  useEffect(() => {
    fetchCategory();
  }, []);

  const fetchCategory = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `https://yelospace.com/api/property-category`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setCategoryList(response?.data?.response);
    } catch (error) {
      console.error("Error fetching property details:", error);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditMode(false);
    setCategory("");
    setSubcategories([{ subCategory: "", subCategoryId: null }]);
    setImage(null);
    setImagePreview("");
    setError("");
    fetchCategory();
  };

  const handleAddSubcategory = () => {
    if (subcategories[subcategories.length - 1].subCategory !== "") {
      setSubcategories([...subcategories, { subCategory: "", subCategoryId: null }]);
    }
  };

  const handleSubcategoryChange = (index, value) => {
    const updatedSubcategories = [...subcategories];
    updatedSubcategories[index].subCategory = value;
    setSubcategories(updatedSubcategories);
  };

  const handleAddCategory = async () => {
    const token = localStorage.getItem("token");

    if (category.trim() === "") {
      setError("Please add a category");
      return;
    }

    if (subcategories.filter((subcategory) => subcategory.subCategory.trim() !== "").length === 0) {
      setError("Please add at least one subcategory");
      return;
    }

    try {
      const formData = new FormData();
      const data = editMode
        ? { category: category, categoryId: selectedCategory.categoryId }
        : { category: category };

      formData.append("data", JSON.stringify(data));
      if (image) {
        formData.append("file", image);
      }

      if (editMode) {
        // Update existing category
        await axios.post(
          `https://admin.yelospace.com/create-edit-category`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );

        // Update subcategories
        const subcategoryRequests = subcategories
          .filter((subcategory) => subcategory.subCategory.trim() !== "")
          .map((subcategory) => {
            const subcategoryData = {
              subCategory: subcategory.subCategory,
              categoryId: selectedCategory.categoryId,
            };
            if (subcategory.subCategoryId) {
              subcategoryData.subCategoryId = subcategory.subCategoryId;
            }
            return axios.post(
              "https://admin.yelospace.com/create-edit-subcategory",
              subcategoryData,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
                },
              }
            );
          });

        await Promise.all(subcategoryRequests);
      } else {
        // Create new category
        const categoryResponse = await axios.post(
          "https://admin.yelospace.com/create-edit-category",
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );
        const categoryId = categoryResponse.data.response.categoryId;

        const subcategoryRequests = subcategories
          .filter((subcategory) => subcategory.subCategory.trim() !== "")
          .map((subcategory) =>
            axios.post(
              "https://admin.yelospace.com/create-edit-subcategory",
              {
                subCategory: subcategory.subCategory,
                categoryId: categoryId,
              },
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
                },
              }
            )
          );

        await Promise.all(subcategoryRequests);
      }

      await fetchCategory();
      handleCloseModal();
    } catch (error) {
      console.error("Failed to create or edit category or subcategory", error);
    }
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    setImage(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleMenuClick = (event, category) => {
    setAnchorEl(event.currentTarget);
    setSelectedCategory(category);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEditCategory = () => {
    handleMenuClose();
    handleOpenEditModal(selectedCategory);
  };

  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = categoryList.slice(indexOfFirstUser, indexOfLastUser);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <Container style={{ display: "flex", marginTop: "20px" }}>
      <Sidebar />
      <div style={{ flex: "1", marginLeft: "20vw" }}>
        <Typography
          variant="h6"
          style={{
            color: "#F5C000",
            fontWeight: "bold",
            marginBottom: "12px",
            marginTop: "24px",
          }}
        >
          Category Management
        </Typography>
        <Paper
          elevation={0}
          style={{ width: "100%", border: "1px solid #fee9c5" }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "12px",
              borderBottom: "1px solid #F5C000",
            }}
          >
            <Typography variant="body1" component="p">
              Categories ({categoryList.length})
            </Typography>
            <div style={{ display: "flex" }}>
              <Button
                variant="outlined"
                style={{
                  color: "#000",
                  borderRadius: "12px",
                  boxShadow: "none",
                  border: "1px solid #F5C000",
                }}
                onClick={handleOpenModal}
              >
                Add Category
              </Button>
            </div>
          </div>
          <Modal
            open={openModal}
            onClose={handleCloseModal}
            aria-labelledby="add-category-modal-title"
            aria-describedby="add-category-modal-description"
          >
            <Paper
              elevation={3}
              style={{
                padding: "20px",
                maxWidth: "400px",
                margin: "100px auto",
                height: "80vh",
                overflowY: "scroll",
              }}
            >
              <Typography variant="h6" gutterBottom>
                {editMode ? "Edit Category" : "Add Category"}
              </Typography>
              <TextField
                label="Category"
                variant="outlined"
                fullWidth
                margin="normal"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              />

              {subcategories.map((subcategory, index) => (
                <TextField
                  key={index}
                  label={`Subcategory ${index + 1}`}
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  value={subcategory.subCategory}
                  onChange={(e) =>
                    handleSubcategoryChange(index, e.target.value)
                  }
                />
              ))}

              {!editMode && (
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleAddSubcategory}
                  style={{ marginTop: "20px", color: "#000" }}
                  disabled={subcategories[subcategories.length - 1].subCategory === ""}
                >
                  Add Subcategory
                </Button>
              )}
              <Box
                sx={{
                  marginTop: "20px",
                  border: "2px dashed #F5C000",
                  borderRadius: "8px",
                  padding: "10px",
                  textAlign: "center",
                  cursor: "pointer",
                }}
              >
                <input
                  type="file"
                  onChange={handleImageChange}
                  style={{ display: "none" }}
                  id="image-upload"
                />
                <label htmlFor="image-upload" style={{ display: "block" }}>
                  {imagePreview ? (
                    <img
                      src={imagePreview}
                      alt="Category Preview"
                      style={{ width: "100%", height: "auto" }}
                    />
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100px",
                      }}
                    >
                      <UploadIcon sx={{ fontSize: 40, color: "#F5C000" }} />
                      <Typography>Click to Upload Category image</Typography>
                    </Box>
                  )}
                </label>
              </Box>
              {error && (
                <Typography
                  variant="body2"
                  color="error"
                  style={{ marginTop: "20px" }}
                >
                  {error}
                </Typography>
              )}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginTop: "20px",
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleAddCategory}
                  style={{
                    color: "#000",
                    backgroundColor: "#F5C000",
                  }}
                >
                  {editMode ? "Save" : "Add"}
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleCloseModal}
                  style={{
                    color: "#000",
                    backgroundColor: "#F5C000",
                  }}
                >
                  Cancel
                </Button>
              </div>
            </Paper>
          </Modal>
          <TableContainer>
            <Table style={{ border: "1.5px solid #fee9c5" }}>
              <TableHead>
                <TableRow style={{ borderBottom: "1.5px solid #fee9c5" }}>
                  <TableCell></TableCell>
                  <TableCell style={{ minWidth: 200 }}>
                    <b>Category</b>
                  </TableCell>
                  <TableCell style={{ minWidth: 200 }}>
                    <b>Sub Category</b>
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentUsers.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell></TableCell>
                    <TableCell style={{ minWidth: 200 }}>
                      {item.category}
                    </TableCell>
                    <TableCell style={{ minWidth: 200 }}>
                      {item.propertySubCategories.map((value, index) => (
                        <div key={index}>{value.subCategory}</div>
                      ))}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        aria-controls="simple-menu"
                        aria-haspopup="true"
                        onClick={(event) => handleMenuClick(event, item)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                      <Menu
                        id="simple-menu"
                        anchorEl={anchorEl}
                        keepMounted
                        open={Boolean(anchorEl)}
                        onClose={handleMenuClose}
                      >
                        <MenuItem onClick={handleEditCategory}>Edit</MenuItem>
                      </Menu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: "20px",
            }}
          >
            {[...Array(Math.ceil(categoryList.length / usersPerPage))].map(
              (_, index) => (
                <Button
                  key={index}
                  variant="contained"
                  style={{
                    borderRadius: "20px",
                    marginRight: "5px",
                    backgroundColor:
                      currentPage === index + 1 ? "#5F5F61" : "white",
                    color: currentPage === index + 1 ? "white" : "#5F5F61",
                  }}
                  onClick={() => paginate(index + 1)}
                >
                  {index + 1}
                </Button>
              )
            )}
          </div>
        </Paper>
      </div>
    </Container>
  );
};

export default CategoryManagement;
